import uuidv4 from 'uuid/v4';
import { mapState, mapMutations } from 'vuex';

import { FlexElement } from '@/data/data-models';
import { classDictionary, FlexClassGroup, FlexClassCollection } from '@/data/tailwind';
import { STATE_CLASSES } from '@/data/constants/classes';
import {
    ADD_SELECTED_ELEMENT,
    CLEAR_COPIED_RENDERING,
    CLEAR_SELECTED_ELEMENTS,
    CLEAR_SELECTED_RENDERING,
    REMOVE_SELECTED_ELEMENT,
    SET_CHANGED_DATA,
    SET_CONTEXT_ELEMENT,
    SET_SELECTED_ELEMENT,
    SET_SELECTED_GROUP,
    SET_SELECTED_LINK,
    SET_SELECTED_RENDERING,
    TOGGLE_GUIDED_SELECT,
    SET_HOLD_EVENTS,
} from '@/store/types/mutation-types';
import { drawerHelperMixin, TYPE_IMAGE } from '@/mixins/drawer-helper';
import { isObject } from '@/utils/object';

export default {
    mixins: [drawerHelperMixin],

    props: {
        id: {
            type: String,
            default: '',
        },

        // for organisms that are inside of a collection
        index: {
            type: Number,
            default: 0,
        },

        // able to move and delete
        isDynamicElement: {
            type: Boolean,
            default: false,
        },

        // able to move and delete
        isDropzoneElement: {
            type: Boolean,
            default: false,
        },

        // used for Sitecore item naming. Items at the same level have to have unique names.
        name: {
            type: String,
            default: '',
        },

        // used for guided select and element toolbar
        editorName: {
            type: String,
            default: '',
        },

        // used for Sitecore field mapping. Atoms are specific, organisms are generic
        elementType: {
            type: String,
            default: '',
        },

        // Pointers are managed by the parent, element should not change self
        // Used to govern order and uniqueness of duplicate elements in Sitecore data
        pointer: {
            type: Number,
            default: -1,
        },

        renderingField: {
            type: String,
            default: '',
        },

        // Sitecore datasource template ID
        templateId: {
            type: String,
            default: '00000000-0000-0000-0000-000000000000',
        },

        // overrides element classes and binds it to data to be modified/saved
        classes: {
            type: String,
            default: '',
        },

        // lets dev pass in default classes in vue files
        classDefault: {
            type: String,
            default: '',
        },

        // set to true to change the root element to an anchor, and to add link-related options
        isLink: {
            type: Boolean,
            default: false,
        },

        // additional properties for specific organism cases
        // update customAttributesNew where needed
        customAttributes: {
            type: Object,
            default: () => ({}),
        },

        htmlCustomAttributes: {
            type: Object,
            default: () => ({}),
        },

        // Link props start
        anchor: {
            type: String,
            default: '',
        },

        href: {
            type: String,
            default: '',
        },

        hrefDefault: {
            type: String,
            default: '#',
        },

        linkType: {
            type: String,
            default: 'internal',
        },

        mediaId: {
            type: String,
            default: '00000000-0000-0000-0000-000000000000',
        },

        queryString: {
            type: String,
            default: '',
        },

        title: {
            type: String,
            default: '',
        },

        target: {
            type: String,
            default: '',
        },

        targetDefault: {
            type: String,
            default: '',
        },
        // Link props end

        // set to true to disable (suppress) the element in the page editor and public build
        isDisabled: {
            type: Boolean,
            default: false,
        },

        // direct children of the page, flex-placeholder, or flex-layout
        topLevel: {
            type: Boolean,
            default: false,
        },

        // top-most placeholder element
        rootLevel: {
            type: Boolean,
            default: false,
        },

        useContext: {
            type: Boolean,
            default: false,
        },

        linkedValueField: {
            type: String,
            default: '',
        },

        isSelectable: {
            type: Boolean,
            default: true,
        },

        isNewElement: {
            type: Boolean,
            default: false,
        },

        ariaLabel: {
            type: String,
            default: null,
        },

        ariaLabelledBy: {
            type: String,
            default: null,
        },

        accessibilityRole: {
            type: String,
            default: null,
        },

        ariaHidden: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            atomicType: '', // the atomic design type ("atom", "organism", or "template")
            cssDisplay: 'flex', // used by the visibility option to apply the correct value when un-hiding an element
            customAttributesNew: {}, // update to change additional props
            htmlCustomAttributesNew: {},
            elementTypeNew: '', // the element type (e.g. "hero-fancy")
            flexClassCollection: {},
            group: [], // an array of elements that are grouped together, all are automatically selected when one is clicked
            groups: {}, // key/value pairs of groups, where they key is the name and the value is the group (see above)
            isHighlighted: false, // set to true to display a green outline and label ("guide toggle")
            isLinkNew: false,
            isSelected: false,
            miscClasses: [], // custom user-added classes
            mounted: false,
            presetId: '',
            templateType: '', // the Sitecore template type
            linkedValueFieldNew: '',
            useContextNew: false,
            linkedComponentNew: '', // for locked/global content - the GUID of the source component
            lockComponentNew: false, // locked components can only be modified from the Component Editor
            boxModelDisplayed: false, // set to true when the CSS box model visualization is being displayed
            isSelectableNew: true, // set to false to prevent users from selecting the element
            ariaLabelNew: '',
            ariaLabelledByNew: '',
            ariaHiddenNew: false,
            accessibilityRoleNew: '',

            // Link data start (only used if isLink is set)
            initialSetLink: true,
            link: {},
            linkUid: null,
            hrefNew: '',
            anchorNew: '',
            linkTypeNew: '',
            mediaIdNew: '',
            queryStringNew: '',
            targetNew: '',
            titleNew: '',
            // Link data end

            validationErrors: [],
        };
    },

    computed: {
        ...mapState({
            deviceMode: state => state.page.deviceMode,
            guidedMode: state => state.page.guidedMode,
            moving: state => state.page.moving,
            pageData: state => state.page.pageData,
            presets: state => state.page.presets,
            previewMode: state => state.previewMode,
            selectedElements: state => state.page.selectedElements,
            selectedLink: state => state.page.selectedLink,
            selectedRendering: state => state.page.selectedRendering,
            holdEvents: state => state.page.holdEvents,
            dynamicMapPreviewMode: state => state.dynamicMapPreviewMode,
        }),

        pageLevel() {
            // "page level" elements are always two levels beneath the root element
            // <flex-placeholder> => <drop-zone-multi> => [page level]
            const grandParentElement = this.getFlexParent(2);

            return grandParentElement ? grandParentElement.rootLevel : false;
        },

        htmlClass() {
            return this.flexClassArray.concat(this.miscClasses);
        },

        // class string to save to sitecore
        // use computed to cache the join so we
        // don't constantly run the operation
        htmlClassString() {
            return this.flexClassArray.concat(this.trimEditorClasses(this.miscClasses)).join(' ');
        },

        flexClassArray() {
            const flexClassArray = [];

            function fetchValue(value) {
                if (!value) return;
                if (typeof value === 'string') return flexClassArray.push(value);
                Object.values(value).forEach(fetchValue);
            }

            Object.values(this.flexClassCollection).forEach(fetchValue);

            return flexClassArray;
        },

        isReadOnly() {
            if (this.$route.name === 'editor' && this.renderingField === 'shared') {
                if (this.elementType === 'flex-placeholder') {
                    return false; // placeholder only exception
                }
                return true; // all shared renderings in editor
            }
            return false;
        },

        isContentEditable() {
            return !this.isGlobalContent || this.$route.name === 'components';
        },

        isGlobalContent() {
            return this.selectedRendering && this.selectedRendering.globalContent;
        },

        isComponentScreen() {
            return this.$route.name === 'components';
        },

        isTemplateScreen() {
            return this.$route.name === 'templates';
        },

        nameNew() {
            return `${ this.elementTypeNew }_${ this.pointer }`;
        },

        // Add element specific properties in their own component instances
        // using updatedElement()
        updatedBaseElement() {
            return new FlexElement({
                id: this.id,
                name: this.nameNew,
                classes: this.htmlClassString,
                customAttributes: this.customAttributesNew,
                htmlCustomAttributes: this.htmlCustomAttributesNew,
                linkedValueField: this.linkedValueFieldNew,
                useContext: this.useContextNew,
                displayName: this.displayName,
                editorName: this.editorName,
                elementType: this.elementTypeNew,
                globalContent: this.globalContent,
                isNewElement: this.isNewElement,
                linkedComponent: this.linkedComponentNew,
                lockComponent: this.lockComponentNew,
                pointer: this.pointer,
                renderingField: this.renderingField,
                templateId: this.templateId,
                templateType: this.templateType,
                ariaLabel: this.ariaLabelNew,
                ariaLabelledBy: this.ariaLabelledByNew,
                ariaHidden: this.ariaHiddenNew,
                accessibilityRole: this.accessibilityRoleNew,

                // Link data start
                isLink: this.isLinkNew,
                href: this.hrefNew,
                anchor: this.anchorNew,
                linkType: this.linkTypeNew,
                title: this.titleNew,
                target: this.targetNew,
                queryString: this.queryStringNew,
                mediaId: this.mediaIdNew,
                // Link data end
            });
        },

        editLocked() {
            if (!this.selectedRendering) {
                return false;
            }
            return this.selectedRendering.globalContent;
        },

        getParentOrganism() {
            let organism = this.$parent;

            while (organism.$parent && organism.atomicType !== 'organism') {
                organism = organism.$parent;
            }

            return organism;
        },

        parentLock() {
            return this.getParentOrganism.lockComponentNew;
        },

        // override this computed property for element specific properties
        // atoms in their own files, organism in their base files
        updatedElement() {
            return this.updatedBaseElement;
        },

        // Link computed props
        renderHref() {
            if (!this.previewMode) return null;

            let buildHref = '';

            if (this.hrefNew) {
                buildHref = this.hrefNew;

                if (this.linkTypeNew === 'internal') {
                    if (this.queryStringNew && !buildHref.includes(this.queryString)) {
                        buildHref += `?${ this.queryStringNew }`;
                    }
                }
            }

            if (this.anchorNew) {
                if (this.linkTypeNew === 'anchor') {
                    if (buildHref && !buildHref.startsWith('#')) {
                        buildHref = `#${ buildHref }`;
                    } else {
                        buildHref = this.anchorNew.startsWith('#')
                            ? this.anchorNew
                            : `#${ this.anchorNew }`;
                    }
                } else {
                    buildHref += this.anchorNew.startsWith('#')
                        ? this.anchorNew
                        : `#${ this.anchorNew }`;
                }
            }

            return buildHref;
        },

        renderTarget() {
            return this.targetNew || this.targetDefault;
        },

        renderTargetItemId() {
            if (this.linkType !== 'internal' && this.linkType !== 'media') return '';

            return this.mediaIdNew;
        },

        renderTitle() {
            return this.titleNew || '';
        },

        renderRel() {
            return this.targetNew === '_blank'
                ? 'noopener noreferrer'
                : '';
        },
        // Link computed props end

        isValid() {
            return !this.validationErrors || this.validationErrors.length < 1;
        },
    },

    watch: {
        parentLock(val) {
            if (!val) {
                this.lockComponentNew = false;
                this.linkedComponentNew = '';
            }
        },

        pointer() {
            if (this.holdEvents) return;
            this.$emit('element-updated', this.updatedElement);
        },

        async updatedElement(val) {
            if (this.holdEvents) return;
            if (val.id === '') return; // don't update before data is populated
            this.$emit('element-updated', val);

            await this.$nextTick();
            this.validateElement();
        },

        async presetId(id) {
            if (!id) return;

            this.miscClasses = []; // clear non-flex classes on preset change

            const preset = this.presets[this.elementTypeNew].elementPresets.find(p => p.id === id);
            if (preset) {
                this.flexClassCollection = new FlexClassCollection(this.getClassObjectFromString(preset.classes));

                this.setChangedData(true);
                await this.$nextTick();
                this.$root.$emit('flex::preset::update');
            }
        },

        selectedLink(val) {
            if (this.linkUid === val.linkId) {
                this.hrefNew = val.linkData.href;
                this.anchorNew = val.linkData.anchor;
                this.linkTypeNew = val.linkData.linkType;
                this.titleNew = val.linkData.title;
                this.targetNew = val.linkData.target;
                this.queryStringNew = val.linkData.queryString;
                this.mediaIdNew = val.linkData.mediaId;
            }
        },

        selectedElements(selected) {
            if (selected[0] === this) {
                this.selectLink();
            }
        },
    },

    created() {
        this.setInitialElementData(); // needs to be before mount to be accessed in components
        this.initLink();
        this.setDefaultDisplayName();
        this.isSelectableNew = this.isSelectable;
    },

    async mounted() {
        this.setBaseFields();
        this.$el.addEventListener('contextmenu', this.setContext, true);

        if (typeof this.$el.removeAttribute === 'function') {
            this.$el.removeAttribute('class-default-inline');
        }

        if (this.isNewElement) {
            if (this.atomicType === 'atom') this.setPresetIfNoDefaultClass();
            this.selectSelf();
        }

        await this.$nextTick();
        this.mounted = true;
    },

    beforeDestroy() {
        this.$el.removeEventListener('contextmenu', this.setContext);
    },

    methods: {
        ...mapMutations({
            addSelectedElement: ADD_SELECTED_ELEMENT,
            clearCopiedRendering: CLEAR_COPIED_RENDERING,
            clearSelectedElements: CLEAR_SELECTED_ELEMENTS,
            clearSelectedRendering: CLEAR_SELECTED_RENDERING,
            removeSelectedElement: REMOVE_SELECTED_ELEMENT,
            setChangedData: SET_CHANGED_DATA,
            setContextElement: SET_CONTEXT_ELEMENT,
            setSelectedElement: SET_SELECTED_ELEMENT,
            setSelectedGroup: SET_SELECTED_GROUP,
            setSelectedLink: SET_SELECTED_LINK,
            setSelectedRendering: SET_SELECTED_RENDERING,
            toggleGuidedSelect: TOGGLE_GUIDED_SELECT,
            setHoldEvents: SET_HOLD_EVENTS,
        }),

        getFlexParent(numLevels) {
            if (!numLevels) return this;

            let currentElement = this;
            let parentElement = null;
            let levelsPassed = 0;

            while (currentElement.$parent && levelsPassed < numLevels) {
                currentElement = currentElement.$parent;

                if (currentElement.elementType) {
                    parentElement = currentElement;
                    levelsPassed++;
                }
            }

            return levelsPassed >= numLevels ? parentElement : null;
        },

        clearLink() {
            this.hrefNew = '';
            this.linkTypeNew = 'internal';
            this.anchorNew = '';
            this.targetNew = '';
            this.queryStringNew = '';
            this.titleNew = '';
            this.mediaIdNew = '';
        },

        clearClasses() {
            this.flexClassCollection = new FlexClassCollection();
        },

        /**
         * builds class object from class string, pushes all
         * other classes into reference array
         * @param {String} str class string
         */
        getClassObjectFromString(str) {
            if (!str || str.trim() === 'null') return {};

            // remove any carriage returns
            let trimmed = str.replace(/[\n|\r]+/g, ' ');

            // remove any extra spaces
            trimmed = trimmed.replace(/  +/g, ' ');

            const classObj = trimmed.split(' ').reduce((obj, className) => {
                const classDefinition = classDictionary[className];

                if (!classDefinition) {
                    if (
                        className
                        && className !== 'null'
                        && !className.startsWith('f-')
                        && !className.startsWith('fu-')
                    ) {
                        this.miscClasses.push(className);
                    }

                    return obj;
                }

                const { screen, side, state, tailwindDefinitionProp } = classDefinition;

                obj[tailwindDefinitionProp] = obj[tailwindDefinitionProp] || new FlexClassGroup({});

                switch (true) {
                    case !!screen && !!state:
                        obj[tailwindDefinitionProp][screen][state] = className;
                        break;

                    case !!screen && !!side:
                        obj[tailwindDefinitionProp][screen][side] = className;
                        break;

                    case !!screen:
                        obj[tailwindDefinitionProp][screen].default = className;
                        break;

                    case !!state:
                        obj[tailwindDefinitionProp][state] = className;
                        break;

                    case !!side:
                        obj[tailwindDefinitionProp][side] = className;
                        break;

                    default:
                        console.warn(`Unable to assign "${ className }" to a flex class group.`);
                }

                return obj;
            }, {});

            return classObj;
        },

        getElementData(key) {
            if (!this.customAttributesNew[key]) return '';

            return JSON.parse(this.customAttributesNew[key]);
        },

        setElementData(key, value, options = null) {
            const optionsNew = {
                triggerChangeDetection: true,
                ...options,
            };

            value = JSON.stringify(value);

            this.$set(this.customAttributesNew, key, value);

            if (optionsNew.triggerChangeDetection) {
                this.setChangedData(true);
            }
        },

        // merges a new data object with the existing one, if one exists
        // note that the data is still stored as a string (JSON) in Sitecore
        mergeElementData(key, newData, options = null) {
            const existingData = this.getElementData(key);
            const optionsNew = {
                triggerChangeDetection: true,
                ...options,
            };

            if (!isObject(newData)) {
                console.warn('Element data merge failed. Please pass in a valid object.');
                return;
            }

            if (!isObject(existingData)) {
                console.warn('Element data merge failed. The existing data is not an object.');
                return;
            }

            this.$set(this.customAttributesNew, key, JSON.stringify({
                ...existingData,
                ...newData,
            }));

            if (optionsNew.triggerChangeDetection) {
                this.setChangedData(true);
            }
        },

        removeElementData(key, options = null) {
            const optionsNew = {
                triggerChangeDetection: true,
                ...options,
            };

            this.$delete(this.customAttributesNew, key);

            if (optionsNew.triggerChangeDetection) {
                this.setChangedData(true);
            }
        },

        hasElementData(key) {
            return this.customAttributesNew
                && Object.prototype.hasOwnProperty.call(this.customAttributesNew, key);
        },

        setDefaultDisplayName() {
            // set a default display name if one does not already exist
            if (!this.hasElementData('displayName')) {
                this.setElementData('displayName', this.displayName, { triggerChangeDetection: false });
            }
        },

        initLink() {
            this.linkUid = uuidv4();

            this.isLinkNew = this.isLink || this.elementType === 'a-link';
            if (!this.isLinkNew) return;

            let href = this.href;
            let anchor = this.anchor;
            if (href.includes('#')) {
                [href, anchor] = href.split('#');
            }

            this.hrefNew = href;
            this.anchorNew = anchor;
            this.linkTypeNew = this.linkType;
            this.titleNew = this.title;
            this.targetNew = this.target;
            this.queryStringNew = this.queryString;
            this.mediaIdNew = this.mediaId;
        },

        onSetElementSelection(e) {
            if (this.guidedMode && !this.getParentOrganism.$el.classList.contains(STATE_CLASSES.HIGHLIGHTED)) {
                this.clearSelectedRendering();
            }

            // first, set the selected organism and selected rendering
            let current = this;
            let rendering = null;

            do {
                if (
                    !rendering
                    && current.topLevel
                ) {
                    rendering = current;
                }

                current = current.$parent;
            } while (current);

            this.setSelectedRendering({ rendering });

            // then set the selected element(s)
            switch (true) {
                case !!this.selectedElements.length && (!!e.ctrlKey || !!e.metaKey):
                    if (this.isSelected) {
                        this.removeSelectedElement({ element: this });
                    } else {
                        this.addSelectedElement({ element: this });
                    }

                    break;

                case !!this.group.length:
                    this.setSelectedGroup({ element: this });

                    break;

                default:
                    this.setSelectedElement({ element: this });
            }
        },

        // if overridden, make sure to copy the code in this method into the override:
        onClick(e) {
            if (this.dynamicMapPreviewMode) {
                this.handleDynamicMapClick(e);
            } else {
                this.handlePageEditClick(e);
            }
        },

        handlePageEditClick(e) {
            if (this.holdEvents) {
                this.setHoldEvents(false);
            }
            if (this.isSelectable) {
                this.onSetElementSelection(e);
                e.stopPropagation();
            }

            // flex-element swallows the clicks, thus preventing context menu from closing:
            this.$root.$emit('flex::context::close');

            // cancel movement mode if a new element is selected
            if (this.moving) {
                this.clearCopiedRendering();
            }
        },

        handleDynamicMapClick() {
            this.$root.$emit('flex::dynamicMap::click', this.$el?.id);
        },

        onDoubleClick() {
            if (this.dynamicMapPreviewMode) {
                this.$root.$emit('flex::dynamicMap::doubleClick', this.$el?.id);
            } else if (this.hasElementData('background-image-url')) {
                this.openMediaDrawer(TYPE_IMAGE);
            }
        },

        setBaseFields() {
            if (!this.elementType) {
                this.elementTypeNew = this.$options._componentTag;
            } else {
                this.elementTypeNew = this.elementType;
            }

            // some default classes are set/fetched from the element itself
            // ... instead of passed via props
            let defaultClassesInline;

            if (typeof this.$el.getAttribute === 'function') {
                defaultClassesInline = this.$el.getAttribute('class-default-inline');
            }

            const defaultClasses = `${ defaultClassesInline } ${ this.classDefault }`;
            const sitecoreClasses = this.classes;
            let classData = {};

            if (sitecoreClasses) {
                classData = this.getClassObjectFromString(sitecoreClasses);
            } else if (defaultClasses) {
                classData = this.getClassObjectFromString(defaultClasses);
            } else {
                classData = {};
            }


            this.flexClassCollection = new FlexClassCollection(classData);
            this.lockComponentNew = this.lockComponent;
            this.linkedComponentNew = this.linkedComponent;
            this.linkedValueFieldNew = this.linkedValueField;
            this.useContextNew = this.useContext;
        },

        setContext() {
            this.setContextElement({ element: this });
        },

        setInitialElementData() {
            this.customAttributesNew = this.customAttributes;
            this.htmlCustomAttributesNew = this.htmlCustomAttributes;
            this.ariaLabelNew = this.ariaLabel;
            this.ariaLabelledByNew = this.ariaLabelledBy;
            this.ariaHiddenNew = this.ariaHidden;
            this.accessibilityRoleNew = this.accessibilityRole;
        },

        selectLink() {
            const val = {
                linkId: this.linkUid,
                linkData: {
                    id: this.id,
                    href: this.hrefNew,
                    linkType: this.linkTypeNew,
                    anchor: this.anchorNew,
                    target: this.targetNew,
                    queryString: this.queryStringNew,
                    title: this.titleNew,
                    mediaId: this.mediaIdNew,
                },
            };

            this.setSelectedLink(val);
        },

        setPresetIfNoDefaultClass() {
            if (!this.presets || !Object.prototype.hasOwnProperty.call(this.presets, this.elementTypeNew)) return;

            this.presetId = this.presets[this.elementTypeNew].defaultPresetId;
            this.setElementData('presetId', this.presets[this.elementTypeNew].defaultPresetId);
        },

        selectSelf() {
            this.setSelectedElement({ element: this });
        },

        trimEditorClasses(miscClassArray) {
            const newArray = miscClassArray.slice(0);

            const index = newArray.indexOf(STATE_CLASSES.SELECTED_ELEMENT);
            if (index > -1) {
                newArray.splice(index, 1);
            }
            const index2 = newArray.indexOf('always-enabled');
            if (index2 > -1) {
                newArray.splice(index2, 1);
            }
            const index3 = newArray.indexOf(STATE_CLASSES.STATIC);
            if (index3 > -1) {
                newArray.splice(index3, 1);
            }

            return newArray;
        },

        validateElement() {
            // overwritten by inheritance
        },

        addValidationError(element, message) {
            this.validationErrors.push(`${ element.customAttributes.displayName?.replace(/"/g, '') } - ${ message }`);
        },
    },
};
